import React, { useEffect, useState } from 'react';
import QuizComponent from './QuizComponent';
import { useUpdateCourseFeaturesMutation } from '../redux/apiSlice';

const VideoItem = ({
  sectionIndex,
  videoIndex,
  video,
  handleVideoInSectionChange,
  handleUploadVideoToServer,
  handleUploadPdfToServer,
  handleDeleteVideo,
  handleDeletePdf,
  handleRemoveVideoFromSection,
  showToast,
  isNested = false,
  nestedSectionIndex = null,
  handleViewPdf,
  courseId,
  refetchCourse
}) => {
  const [updateCourseFeatures] = useUpdateCourseFeaturesMutation();
  const [isLockUpdating, setIsLockUpdating] = useState(false);

  useEffect(() => {
    console.log('📄 VideoItem PDF State:', {
      videoId: video.id,
      videoTitle: video.title,
      hasServerPdfUrl: !!video.serverPdfUrl,
      serverPdfUrl: video.serverPdfUrl,
      hasPdfFile: !!video.pdfFile,
      pdfUploadStatus: video.pdfUploadStatus,
      pdfTitle: video.pdfTitle
    });
  }, [video.id, video.title, video.serverPdfUrl, video.pdfFile, video.pdfUploadStatus, video.pdfTitle]);

  useEffect(() => {
    console.log('🎬 VideoItem - Video quizzes:', {
      videoId: video.id,
      videoTitle: video.title,
      quizzes: video.quizzes,
      InstructorQuiz: video.InstructorQuiz,
      hasQuizzes: !!(video.quizzes && video.quizzes.length > 0),
      hasInstructorQuiz: !!(video.InstructorQuiz && video.InstructorQuiz.length > 0)
    });
  }, [video]);

  const getActualIndex = () => {
    if (isNested && nestedSectionIndex !== null) {
      return {
        parentSectionIndex: sectionIndex,
        nestedSectionIndex: nestedSectionIndex,
        videoIndex: videoIndex,
        isNested: true
      };
    }
    return {
      sectionIndex: sectionIndex,
      videoIndex: videoIndex,
      isNested: false,
      nestedSectionIndex: null
    };
  };

  const actualIndex = getActualIndex();

  const updateVideoField = (field, value) => {
    if (actualIndex.isNested) {
      handleVideoInSectionChange(
        actualIndex.parentSectionIndex,
        actualIndex.videoIndex,
        field,
        value,
        actualIndex.isNested,
        actualIndex.nestedSectionIndex
      );
    } else {
      handleVideoInSectionChange(
        actualIndex.sectionIndex,
        actualIndex.videoIndex,
        field,
        value,
        actualIndex.isNested
      );
    }
  };

  const handleViewPdfClick = () => {
    if (!video.serverPdfUrl) {
      showToast('PDF URL not found. Please upload PDF first.', 'error');
      return;
    }
    
    if (handleViewPdf) {
      handleViewPdf(video.serverPdfUrl, video.title);
    } else {
      if (
        video.serverPdfUrl.startsWith('http://') ||
        video.serverPdfUrl.startsWith('https://') ||
        video.serverPdfUrl.startsWith('blob:')
      ) {
        window.open(video.serverPdfUrl, '_blank', 'noopener,noreferrer');
        showToast(`Opening PDF for "${video.title}" in new tab...`, 'info');
      } else {
        showToast('Cannot open PDF: Invalid URL format', 'error');
      }
    }
  };

  const handleRetryPdfUpload = () => {
    if (!video.pdfFile) {
      showToast('No PDF file found to retry upload', 'error');
      return;
    }
    
    if (!video.serverVideoId) {
      showToast('Video ID is missing. Please make sure video upload completed successfully.', 'error');
      return;
    }

    const pdfNameToUse =
      (video.pdfTitle || '').trim() ||
      video.pdfFile.name.replace(/\.pdf$/i, '');

    if (!pdfNameToUse) {
      showToast('Please enter PDF name before upload.', 'error');
      return;
    }
    
    if (actualIndex.isNested) {
      handleUploadPdfToServer(
        actualIndex.parentSectionIndex,
        actualIndex.videoIndex,
        actualIndex.isNested,
        actualIndex.nestedSectionIndex,
        false
      );
    } else {
      handleUploadPdfToServer(
        actualIndex.sectionIndex,
        actualIndex.videoIndex,
        actualIndex.isNested,
        null,
        false
      );
    }
  };

  const handleDeletePdfWithConfirm = async () => {
    if (window.confirm('Are you sure you want to delete this PDF?')) {
      if (actualIndex.isNested) {
        await handleDeletePdf(
          actualIndex.parentSectionIndex,
          actualIndex.videoIndex,
          actualIndex.isNested,
          actualIndex.nestedSectionIndex
        );
      } else {
        await handleDeletePdf(
          actualIndex.sectionIndex,
          actualIndex.videoIndex,
          actualIndex.isNested
        );
      }
    }
  };

  const handleDeleteVideoWithConfirm = async () => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      if (actualIndex.isNested) {
        await handleDeleteVideo(
          actualIndex.parentSectionIndex,
          actualIndex.videoIndex,
          actualIndex.isNested,
          actualIndex.nestedSectionIndex
        );
      } else {
        await handleDeleteVideo(
          actualIndex.sectionIndex,
          actualIndex.videoIndex,
          actualIndex.isNested
        );
      }
    }
  };

  const handleRemoveVideoWithConfirm = async () => {
    if (window.confirm('Are you sure you want to remove this video?')) {
      if (actualIndex.isNested) {
        await handleRemoveVideoFromSection(
          actualIndex.parentSectionIndex,
          actualIndex.videoIndex,
          actualIndex.isNested,
          actualIndex.nestedSectionIndex
        );
      } else {
        await handleRemoveVideoFromSection(
          actualIndex.sectionIndex,
          actualIndex.videoIndex,
          actualIndex.isNested
        );
      }
    }
  };

  const handleUploadVideoClick = () => {
    if (actualIndex.isNested) {
      handleUploadVideoToServer(
        actualIndex.parentSectionIndex,
        actualIndex.videoIndex,
        actualIndex.isNested,
        actualIndex.nestedSectionIndex
      );
    } else {
      handleUploadVideoToServer(
        actualIndex.sectionIndex,
        actualIndex.videoIndex,
        actualIndex.isNested
      );
    }
  };

  const handleLockToggle = async (value) => {
    const newLockStatus = value === 'lock';
    
    updateVideoField('isLocked', newLockStatus);
    
    if (video.serverVideoId && courseId) {
      setIsLockUpdating(true);
      
      try {
        const result = await updateCourseFeatures({
          courseId: courseId,
          videoIds: [{
            id: parseInt(video.serverVideoId, 10),
            islock: newLockStatus
          }],
          pdfIds: [],
          quizIds: []
        }).unwrap();
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (refetchCourse && typeof refetchCourse === 'function') {
          try {
            await refetchCourse();
          } catch (refetchError) {
            console.log('⚠️ Could not refetch course:', refetchError.message);
          }
        }
        
        showToast(`Video "${video.title}" ${newLockStatus ? 'locked' : 'unlocked'} successfully`, 'success');
      } catch (error) {
        console.error('❌ Failed to update video lock status:', error);
        updateVideoField('isLocked', !newLockStatus);
        
        let errorMessage = 'Failed to update lock status. ';
        if (error.data?.message) {
          errorMessage += error.data.message;
        } else {
          errorMessage += 'Please try again.';
        }
        
        showToast(errorMessage, 'error');
      } finally {
        setIsLockUpdating(false);
      }
    } else {
      showToast(`Video lock status set to ${newLockStatus ? 'locked' : 'unlocked'} (will apply after upload)`, 'info');
    }
  };

  const getAllQuizzes = () => {
    const allQuizzes = [];
    
    if (video.quizzes && Array.isArray(video.quizzes) && video.quizzes.length > 0) {
      video.quizzes.forEach(quiz => allQuizzes.push(quiz));
    }
    
    if (video.InstructorQuiz && Array.isArray(video.InstructorQuiz) && video.InstructorQuiz.length > 0) {
      video.InstructorQuiz.forEach(quiz => allQuizzes.push(quiz));
    }
    
    return allQuizzes;
  };

  const displayedPdfName =
    video.pdfTitle !== undefined && video.pdfTitle !== null
      ? video.pdfTitle
      : (video.pdfFile ? video.pdfFile.name.replace(/\.pdf$/i, '') : '');

  const finalPdfUploadName =
    (video.pdfTitle || '').trim() ||
    (video.pdfFile ? video.pdfFile.name.replace(/\.pdf$/i, '') : '');

  return (
    <div key={video.id} className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          </svg>
        </div>

        <div className="flex-1 min-w-0">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Video Title *
                </label>
                <input
                  type="text"
                  value={video.title || ''}
                  onChange={(e) => updateVideoField('title', e.target.value)}
                  placeholder="Enter video title"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div className="flex items-center space-x-4 bg-gray-50 p-2 rounded-lg">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name={`lock-status-${video.id}`}
                    value="unlock"
                    checked={!video.isLocked}
                    onChange={() => handleLockToggle('unlock')}
                    disabled={isLockUpdating}
                    className="w-4 h-4 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">Unlock</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name={`lock-status-${video.id}`}
                    value="lock"
                    checked={video.isLocked}
                    onChange={() => handleLockToggle('lock')}
                    disabled={isLockUpdating}
                    className="w-4 h-4 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">Lock</span>
                </label>
                {isLockUpdating && (
                  <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin ml-2"></div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={video.description || ''}
                onChange={(e) => updateVideoField('description', e.target.value)}
                placeholder="Brief description of this video content"
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
            </div>

            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-sm font-semibold text-gray-700 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  PDF Notes
                </h5>
              </div>
              
              <div className="space-y-4">
                {video.serverPdfUrl ? (
                  <div className="bg-white border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-gray-900">PDF Uploaded Successfully</p>
                          <p className="text-xs text-gray-500">
                            Ready to view • {video.serverPdfUrl.substring(0, 30)}...
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={handleViewPdfClick}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors duration-300"
                        >
                          View PDF
                        </button>
                        
                        <button
                          type="button"
                          onClick={handleDeletePdfWithConfirm}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors duration-300"
                          disabled={video.pdfDeleteStatus === 'deleting'}
                        >
                          Delete PDF
                        </button>
                      </div>
                    </div>
                  </div>
                ) : video.pdfFile ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-3">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{video.pdfFile.name}</p>
                            <p className="text-xs text-gray-500">
                              {(video.pdfFile.size / (1024 * 1024)).toFixed(2)} MB • Ready to upload
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {video.pdfUploadStatus === 'uploading' ? (
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                              <span className="text-xs text-blue-600">Uploading...</span>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={handleRetryPdfUpload}
                              className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors duration-300"
                              disabled={!video.serverVideoId}
                            >
                              {video.serverVideoId ? 'Upload PDF' : 'Need Video ID'}
                            </button>
                          )}
                          
                          <button
                            type="button"
                            onClick={() => updateVideoField('pdfFile', null)}
                            className="px-4 py-2 text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          PDF Name Before Upload *
                        </label>
                        <input
                          type="text"
                          value={displayedPdfName}
                          onChange={(e) => updateVideoField('pdfTitle', e.target.value)}
                          placeholder="Enter PDF file name"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Final upload name: {finalPdfUploadName ? `${finalPdfUploadName}.pdf` : (video.pdfFile?.name || 'document.pdf')}
                        </p>
                      </div>
                    </div>
                    
                    {!video.serverVideoId && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                        <p className="text-xs text-yellow-700">
                          ⚠️ Please upload the video first to get a Video ID, then you can upload this PDF.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition-all duration-300">
                    <svg className="w-10 h-10 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm text-gray-600 mb-4">Add PDF notes for this video (optional)</p>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => {
                        const selectedPdf = e.target.files?.[0];
                        if (selectedPdf) {
                          updateVideoField('pdfFile', selectedPdf);
                        }
                        e.target.value = '';
                      }}
                      className="hidden"
                      id={`pdf-upload-${video.id}`}
                    />
                    <label
                      htmlFor={`pdf-upload-${video.id}`}
                      className="px-5 py-2.5 bg-blue-500 text-white rounded-lg font-medium cursor-pointer transition-all duration-300 hover:bg-blue-600 inline-block text-sm"
                    >
                      Select PDF File
                    </label>
                  </div>
                )}
                
                {video.pdfUploadStatus === 'uploading' && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                    <div className="flex items-center space-x-3">
                      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <div>
                        <span className="text-sm text-blue-600 font-medium">Uploading PDF to server...</span>
                        <p className="text-xs text-gray-500 mt-1">Please wait while we upload your PDF file.</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {video.pdfUploadStatus === 'error' && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <div>
                          <span className="text-sm text-red-600 font-medium">PDF Upload Failed</span>
                          <p className="text-xs text-gray-500 mt-1">Please try uploading again.</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRetryPdfUpload}
                        className="px-3 py-1.5 bg-red-500 text-white rounded text-sm hover:bg-red-600 transition-colors duration-300"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-500">
              <span className="truncate">
                Video: {video.file?.name || (video.serverVideoId ? 'Uploaded Video' : 'No video')}
              </span>
              <span>Duration: {video.duration}</span>
              <span>Size: {video.file ? (video.file.size / (1024 * 1024)).toFixed(2) + ' MB' : 'N/A'}</span>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {video.uploadStatus === 'uploading' && (
                    <div className="flex items-center space-x-2 text-blue-600">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs font-medium">Uploading to Vimeo...</span>
                    </div>
                  )}
                  {video.uploadStatus === 'success' && (
                    <div className="flex items-center space-x-2 text-green-600">
                      <span className="text-xs font-medium">Video uploaded to Vimeo successfully</span>
                    </div>
                  )}
                  {video.uploadStatus === 'error' && (
                    <div className="flex items-center space-x-2 text-red-600">
                      <span className="text-xs font-medium">Vimeo Upload Failed</span>
                    </div>
                  )}
                  {!video.uploadStatus && video.file && (
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500">Ready to upload to Vimeo</span>
                      <span className="text-xs text-gray-400">Current lock status: {video.isLocked ? 'Locked' : 'Unlocked'}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  {video.file && !video.serverVideoId && (
                    <button
                      type="button"
                      onClick={handleUploadVideoClick}
                      disabled={video.uploadStatus !== null}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {video.uploadStatus === 'uploading' ? 'Uploading...' : 'Upload to Vimeo'}
                    </button>
                  )}

                  {video.serverVideoId && (
                    <button
                      type="button"
                      onClick={handleDeleteVideoWithConfirm}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium text-sm hover:bg-red-600 transition-colors duration-300"
                      disabled={video.deleteStatus === 'deleting'}
                    >
                      {video.deleteStatus === 'deleting' ? 'Deleting...' : 'Delete Video'}
                    </button>
                  )}
                </div>
              </div>
              
              {video.uploadProgress > 0 && (
                <div className="mt-3">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${video.uploadProgress}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {video.uploadProgress}% uploaded to Vimeo
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100">
              <QuizComponent 
                videoId={video.serverVideoId || `temp-video-${video.id}`}
                videoTitle={video.title || 'Untitled Video'}
                showToast={showToast}
                existingQuizzes={getAllQuizzes()}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col space-y-2">
          {!video.serverVideoId && (
            <button
              type="button"
              onClick={handleRemoveVideoWithConfirm}
              className="flex-shrink-0 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Remove video"
              disabled={video.uploadStatus === 'uploading' || video.pdfUploadStatus === 'uploading'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoItem;